import { useMemo } from 'react';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';

import {
  useFetchTicketAreas,
  useFetchTicketTipos,
  useToggleAreaIncluirLegacy,
  useToggleAreaIncluirCierreLegacy,
} from '@/shared/actions/erp/tickets-soporte-cliente/gestion-tickets-catalogos.actions';
import type {
  ITicketArea,
  ITicketTipo,
} from '@/shared/interfaces/erp/tickets-soporte-clientes';

/**
 * Panel de administracion del arbol Tipo/Categoria/Subcategoria por area.
 * Solo se renderiza para usuarios con permiso catalogos.toggle_legacy_por_area
 * (el padre CatalogosPage controla la visibilidad). Aqui adentro asumimos
 * que ya pasamos el guard.
 *
 * Dos secciones:
 *   1) Tabla de areas con switch "Incluir tipos legacy".
 *      Cuando ON (default) el form de crear ticket muestra para esa area
 *      sus tipos propios + los legacy. Cuando OFF solo los propios.
 *      Apagar cuando el supervisor del area termino de migrar su arbol.
 *
 *   2) Lista de "Tipos Legacy" (los que tienen area_id=NULL en BD).
 *      Solo informativo + accion rapida sugerida: editar el tipo desde
 *      el tab "Tipos" y asignarle area. A medida que se asignen, salen
 *      de esta lista.
 */
export default function PanelConfiguracionArbol() {
  const { data: areasRes, isLoading: loadingAreas } = useFetchTicketAreas();
  const { data: tiposRes, isLoading: loadingTipos } = useFetchTicketTipos();
  const toggleAreaMutation = useToggleAreaIncluirLegacy();
  const toggleCierreLegacyMutation = useToggleAreaIncluirCierreLegacy();

  const areas: ITicketArea[] = useMemo(
    () => (areasRes as any)?.data ?? [],
    [areasRes],
  );
  const tipos: ITicketTipo[] = useMemo(
    () => (tiposRes as any)?.data ?? [],
    [tiposRes],
  );

  const tiposLegacy = useMemo(
    () => tipos.filter((t: any) => !t.area && !t.area_id),
    [tipos],
  );

  const handleToggle = (area: ITicketArea, nuevo: boolean) => {
    toggleAreaMutation.mutate({ id: area.id_area, incluir: nuevo });
  };

  const handleToggleCierre = (area: ITicketArea, nuevo: boolean) => {
    toggleCierreLegacyMutation.mutate({ id: area.id_area, incluir: nuevo });
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <SettingsSuggestIcon color="primary" />
        <Typography variant="h6" fontWeight={700}>
          Configuración del árbol Tipo / Categoría / Subcategoría por área
        </Typography>
      </Stack>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <strong>Legacy Apertura</strong>: controla el árbol Tipo/Categoría/Subcategoría usado al{' '}
          <em>crear</em> tickets. Cuando está <strong>ON</strong> (default) el form muestra tipos
          propios del área + tipos legacy (área NULL). Cuando está <strong>OFF</strong> solo los
          propios.
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <strong>Legacy Cierre</strong>: controla el árbol Categoría/Subcategoría de cierre usado
          al <em>cerrar</em> tickets. Cuando está <strong>ON</strong> (default) el modal de cerrar
          muestra categorías propias + las default (genéricas globales). Cuando está{' '}
          <strong>OFF</strong> solo las propias.
        </Typography>
        <Typography variant="body2">
          Los 2 toggles son <strong>independientes</strong>: una área puede ya no usar tipos
          legacy de apertura pero seguir mostrando los motivos default de cierre, y viceversa.
        </Typography>
      </Alert>

      <Paper variant="outlined" sx={{ mb: 4 }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Áreas ({areas.length})
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Área</TableCell>
                <TableCell>Código</TableCell>
                <TableCell align="center">Legacy Apertura</TableCell>
                <TableCell align="center">Legacy Cierre</TableCell>
                <TableCell>Estado del árbol</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingAreas && (
                <TableRow>
                  <TableCell colSpan={5} align="center">Cargando áreas...</TableCell>
                </TableRow>
              )}
              {!loadingAreas && areas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <em>No hay áreas configuradas</em>
                  </TableCell>
                </TableRow>
              )}
              {areas.map((a) => {
                const incluirAp = a.incluir_tipos_legacy !== false;
                const incluirCi = a.incluir_cierre_legacy !== false;
                const tiposDelArea = tipos.filter(
                  (t: any) =>
                    t.area?.id_area === a.id_area || t.area_id === a.id_area,
                );
                return (
                  <TableRow key={a.id_area} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {a.color && (
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: a.color,
                              flexShrink: 0,
                            }}
                          />
                        )}
                        <Typography variant="body2" fontWeight={500}>
                          {a.nombre}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {a.codigo}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={incluirAp}
                        onChange={(e) => handleToggle(a, e.target.checked)}
                        disabled={toggleAreaMutation.isPending}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={incluirCi}
                        onChange={(e) => handleToggleCierre(a, e.target.checked)}
                        disabled={toggleCierreLegacyMutation.isPending}
                        color="secondary"
                      />
                    </TableCell>
                    <TableCell>
                      {tiposDelArea.length > 0 ? (
                        <Chip
                          label={`${tiposDelArea.length} tipo(s) propios`}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          label="Sin tipos propios — usa legacy"
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper variant="outlined">
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              Tipos Legacy (sin área asignada) — {tiposLegacy.length}
            </Typography>
            <Chip label="HEREDADOS" size="small" color="warning" />
          </Stack>
        </Box>
        <Box sx={{ p: 2 }}>
          {!loadingTipos && tiposLegacy.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No hay tipos legacy. Cada tipo tiene su área asignada — los filtros ya están
              completos.
            </Typography>
          )}
          {tiposLegacy.length > 0 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Estos tipos aparecen en el filtro de cualquier área que tenga "Incluir Legacy"
                en ON. Para retirarlos del filtro de un área específica, apagá el switch
                arriba. Para asignarlos a un área permanente, editá el tipo desde el tab{' '}
                <strong>Tipos</strong>.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {tiposLegacy.map((t: any) => (
                  <Chip
                    key={t.id_ticket_tipo}
                    label={t.nombre}
                    variant="outlined"
                    size="medium"
                  />
                ))}
              </Stack>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
